import { createContext, useContext } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

/**
 * The Share-as-Post snapshot (`ChartSnapshot`) renders offscreen on a fixed
 * white card, but the charts resolve their colours from the *current* document
 * theme (`resolveColor` reads `document.documentElement`). A dark-mode user
 * therefore got the dark chart palette painted onto the light card — the
 * lines/bars looked washed out and the axis text near-invisible (CORE-6264).
 *
 * This module lets the snapshot force light-theme colour resolution for the
 * charts inside it, without touching the app's actual theme.
 */

/**
 * Resolve a `var(…)` token reference to its **light-theme** computed value,
 * regardless of the theme currently applied to `<html>`. When the document is
 * in dark mode we momentarily drop the `dark` class for a synchronous
 * `getComputedStyle` read, then restore it. There's no `await` (and therefore
 * no paint) between the toggle and the restore, so the live UI never flashes.
 * Non-`var()` values pass through unchanged, mirroring {@link resolveColor}.
 */
export function resolveLightColor(cssVar: string): string {
  if (!cssVar.startsWith('var(')) return cssVar
  const propName = cssVar.slice(4, -1).trim()
  if (typeof document === 'undefined') return ''
  const html = document.documentElement
  const read = (): string =>
    getComputedStyle(html).getPropertyValue(propName).trim()
  // Light is the bare `:root` default (dark tokens live on `:root.dark`), so
  // dropping the class is enough — no need to add a `light` class.
  if (!html.classList.contains('dark')) return read()
  html.classList.remove('dark')
  try {
    return read()
  } finally {
    html.classList.add('dark')
  }
}

/**
 * True inside the offscreen snapshot: charts read this to (a) resolve colours
 * via {@link resolveLightColor} and (b) render both axes' tick labels in the
 * subtle text token (the snapshot renders on a light card, so dates and amounts
 * both use `--color-text-subtle`). Defaults to `false` everywhere else, so the
 * live charts are unaffected.
 */
const SnapshotChartThemeContext = createContext(false)

export function SnapshotChartThemeProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return (
    <SnapshotChartThemeContext.Provider value={true}>
      {children}
    </SnapshotChartThemeContext.Provider>
  )
}

export function useIsSnapshotChartTheme(): boolean {
  return useContext(SnapshotChartThemeContext)
}
